import React, { useState, useEffect } from 'react';
import { X, Edit, User, Mail, Phone, MapPin, FileText, Award, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { notaryService } from '../../../services/notaryService';
import Button from '../../../components/ui/Button';
import NotaryRegulatoryPanel from './NotaryRegulatoryPanel';

const NotaryDetailsModal = ({ notary, onClose, onEdit }) => {
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (notary?.id) {
      loadNotaryAppointments();
    }
  }, [notary?.id]);

  const loadNotaryAppointments = async () => {
    try {
      setLoadingAppointments(true);
      const appointmentsData = await notaryService?.getNotaryAppointments(notary?.id);
      setAppointments(appointmentsData || []);
    } catch (error) {
      console.error('Error loading notary appointments:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Programmé' },
      confirmed: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Confirmé' },
      completed: { color: 'bg-muted/40 text-muted-foreground border-border', label: 'Terminé' },
      cancelled: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Annulé' },
      rescheduled: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Reporté' }
    };
    const config = statusConfig?.[status] || { color: 'bg-muted/40 text-muted-foreground border-border', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatAvailableHours = (hours) => {
    if (!hours || typeof hours !== 'object') return 'Non défini';
    const days = {
      monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
      thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
    };
    return Object.entries(hours)?.map(([day, schedule]) => {
      const dayName = days?.[day] || day;
      if (schedule?.closed) return `${dayName}: Fermé`;
      if (schedule?.start && schedule?.end) return `${dayName}: ${schedule?.start} - ${schedule?.end}`;
      return `${dayName}: Non défini`;
    })?.join(', ');
  };

  const stats = {
    totalAppointments: appointments?.length || 0,
    completedAppointments: appointments?.filter(a => a?.status === 'completed')?.length || 0,
    upcomingAppointments: appointments?.filter(a => a?.status === 'scheduled' || a?.status === 'confirmed')?.length || 0,
    cancelledAppointments: appointments?.filter(a => a?.status === 'cancelled')?.length || 0
  };

  const tabs = [
    { id: 'profile', label: 'Profil' },
    { id: 'appointments', label: `Rendez-vous (${appointments?.length || 0})` },
    { id: 'schedule', label: 'Horaires' },
    { id: 'regulatory', label: 'Supervision Réglementaire' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border border-border w-full max-w-4xl shadow-lg rounded-lg bg-card">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-foreground">
            Détails du Notaire
          </h3>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="w-4 h-4 mr-1" />
              Modifier
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <nav className="flex space-x-8">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab?.id
                    ? 'border-brand-gold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Header with Status */}
            <div className="bg-muted/40 border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 rounded-full bg-muted/40 border border-border flex items-center justify-center">
                    <User className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-foreground">
                      {notary?.user_profile?.full_name || notary?.notary_name || 'N/A'}
                    </h4>
                    <p className="text-muted-foreground">{notary?.user_profile?.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                    notary?.is_verified
                      ? 'bg-green-100 text-green-800 border-green-200' :'bg-yellow-100 text-yellow-800 border-yellow-200'
                  }`}>
                    {notary?.is_verified ? (
                      <><CheckCircle className="w-4 h-4 mr-1" />Vérifié</>
                    ) : (
                      <><AlertCircle className="w-4 h-4 mr-1" />En attente</>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact & Professional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <h5 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Contact</h5>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-muted-foreground mr-3 flex-shrink-0" />
                    <span className="text-sm text-foreground">{notary?.user_profile?.email || 'N/A'}</span>
                  </div>
                  {notary?.user_profile?.phone && (
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-muted-foreground mr-3 flex-shrink-0" />
                      <span className="text-sm text-foreground">{notary?.user_profile?.phone}</span>
                    </div>
                  )}
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-muted-foreground mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{notary?.office_address || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <h5 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Informations Professionnelles</h5>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-muted-foreground mr-3 flex-shrink-0" />
                    <span className="text-sm text-foreground">{notary?.license_number || 'N/A'}</span>
                  </div>
                  <div className="flex items-start">
                    <Award className="w-4 h-4 text-muted-foreground mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      {notary?.specializations?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {notary?.specializations?.map((spec, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted/40 border border-border text-foreground"
                            >
                              {spec}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Aucune spécialisation définie</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total RDV', value: stats?.totalAppointments, color: 'text-blue-500' },
                { label: 'Terminés', value: stats?.completedAppointments, color: 'text-green-500' },
                { label: 'À venir', value: stats?.upcomingAppointments, color: 'text-yellow-500' },
                { label: 'Annulés', value: stats?.cancelledAppointments, color: 'text-destructive' }
              ]?.map((stat, i) => (
                <div key={i} className="bg-muted/40 border border-border rounded-lg p-4 text-center">
                  <div className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{stat?.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="space-y-4">
            {loadingAppointments ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold mx-auto"></div>
                <p className="text-muted-foreground mt-2 text-sm">Chargement des rendez-vous...</p>
              </div>
            ) : appointments?.length > 0 ? (
              <div className="space-y-3">
                {appointments?.map((appointment) => (
                  <div key={appointment?.id} className="bg-card border border-border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">
                            {formatDate(appointment?.scheduled_date)}
                          </span>
                          {getStatusBadge(appointment?.status)}
                        </div>
                        <div className="text-sm text-muted-foreground mb-1">
                          Client: {appointment?.client?.full_name || 'N/A'}
                        </div>
                        {appointment?.property && (
                          <div className="text-sm text-muted-foreground mb-1">
                            Propriété: {appointment?.property?.title}
                          </div>
                        )}
                        {appointment?.appointment_type && (
                          <div className="text-sm text-muted-foreground">
                            Type: {appointment?.appointment_type}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium text-foreground">Aucun rendez-vous</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ce notaire n'a pas encore de rendez-vous programmés.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-6">
              <h5 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide flex items-center">
                <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                Horaires de Disponibilité
              </h5>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {formatAvailableHours(notary?.available_hours)}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h5 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Dates importantes</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-border/50 pb-2">
                  <span className="text-muted-foreground">Créé le</span>
                  <span className="text-foreground font-medium">{formatDate(notary?.created_at)}</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="text-muted-foreground">Dernière mise à jour</span>
                  <span className="text-foreground font-medium">{formatDate(notary?.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regulatory Tab */}
        {activeTab === 'regulatory' && (
          <NotaryRegulatoryPanel notary={notary} />
        )}
      </div>
    </div>
  );
};

export default NotaryDetailsModal;